import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/components/layout/page-title';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { ApiError } from '@/features/auth/api';
import { useInvoices, useRefreshInvoices } from './api';
import { MonthPicker, useInvoiceMonth } from './InvoiceMonth';
import { RefreshButton } from './RefreshButton';
import { useScrollToTop } from './useRefreshList';
import { NextSyncIndicator } from './NextSyncIndicator';
import { InvoicesCardList, InvoicesTable } from './InvoiceTable';
import { StaleBlur } from './StaleBlur';
import type { InvoiceRow, InvoiceSubject } from './types';

/**
 * The Issued / Received routes: a focused list of one subject's invoices for the selected month.
 * Selecting a row opens its full detail page (the subject is uniform here, so no direction badge).
 */
export function InvoiceListPage({ subject }: { subject: InvoiceSubject }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const isMobile = useIsMobile();
  const { month } = useInvoiceMonth();
  const navigate = useNavigate();

  const query = useInvoices(subject, month);
  const refresh = useRefreshInvoices();
  // Data already on screen but fetching a *replacement* set (month switch via keepPreviousData, or a
  // manual refresh) — not a next-page append, which leaves the existing rows in place.
  const isStale = !query.isPending && query.isFetching && !query.isFetchingNextPage;
  // Flatten the loaded pages and drop any KSeF number we've already shown: offset paging can repeat a
  // row across a page boundary if the backing data shifts mid-scroll, and duplicate keys would warn.
  const invoices = useMemo(() => {
    const seen = new Set<string>();
    return (query.data?.pages ?? [])
      .flatMap((page) => page.items)
      .filter((inv) => (seen.has(inv.ksefNumber) ? false : (seen.add(inv.ksefNumber), true)));
  }, [query.data]);
  const rows: InvoiceRow[] = invoices.map((invoice) => ({ invoice, subject }));
  const noCertificate = query.error instanceof ApiError && query.error.status === 400;
  const navKey = subject === 'SELLER' ? 'issued' : 'received';
  usePageTitle(t(`invoices.heading.${navKey}`));
  // Stable identity so the memoized rows don't all re-render when a page is appended.
  const openInvoice = useCallback(
    (invoice: { ksefNumber: string }) =>
      navigate(`/invoices/${navKey}/${encodeURIComponent(invoice.ksefNumber)}`),
    [navigate, navKey],
  );

  // Refresh collapses the list back to page 1, so send the scroll position back up to match.
  const { rootRef, scrollToTop } = useScrollToTop();

  return (
    <div ref={rootRef} className="flex h-full min-h-0 flex-col gap-6">
      <PageHeader
        title={t(`invoices.heading.${navKey}`)}
        description={t(`invoices.description.${navKey}`)}
        actions={
          <>
            <NextSyncIndicator />
            <MonthPicker />
            <RefreshButton {...refresh} onClick={scrollToTop} />
          </>
        }
      />

      {query.isPending ? (
        <Skeleton className={isMobile ? 'h-64 w-full' : 'min-h-0 w-full flex-1'} />
      ) : noCertificate ? (
        <MessageCard isMobile={isMobile}>{t('invoices.no_certificate')}</MessageCard>
      ) : query.isError ? (
        <MessageCard isMobile={isMobile} tone="destructive">
          {t('invoices.load_error')}
        </MessageCard>
      ) : invoices.length === 0 ? (
        <MessageCard isMobile={isMobile}>{t('invoices.empty')}</MessageCard>
      ) : (
        <StaleBlur stale={isStale} className="flex min-h-0 flex-1 flex-col">
          {/* The toolbar above stays put; the list fills the remaining height and scrolls inside its
              own region, so the page never grows a native scrollbar and the month picker never scrolls
              away — matching the Summary page. The table brings its own ScrollArea; the mobile card
              list is wrapped in one here. */}
          <Card className="min-h-0 flex-1">
            <CardContent className="flex min-h-0 flex-1 flex-col">
              {isMobile ? (
                <ScrollArea className="cn-vertical-scroll min-h-0 flex-1">
                  <InvoicesCardList
                    rows={rows}
                    lng={lng}
                    onSelect={openInvoice}
                    hasMore={query.hasNextPage}
                    isLoadingMore={query.isFetchingNextPage}
                    onLoadMore={query.fetchNextPage}
                  />
                </ScrollArea>
              ) : (
                <InvoicesTable
                  rows={rows}
                  lng={lng}
                  onSelect={openInvoice}
                  hasMore={query.hasNextPage}
                  isLoadingMore={query.isFetchingNextPage}
                  onLoadMore={query.fetchNextPage}
                />
              )}
            </CardContent>
          </Card>
        </StaleBlur>
      )}
    </div>
  );
}

/**
 * A status message (empty / error / no-certificate) that occupies the same height as the populated
 * list on desktop, so the container size never changes with content. Stays natural-flow on mobile,
 * where the page scrolls rather than the list.
 */
function MessageCard({
  isMobile,
  tone = 'muted',
  children,
}: {
  isMobile: boolean;
  tone?: 'muted' | 'destructive';
  children: ReactNode;
}) {
  return (
    <Card className={isMobile ? undefined : 'min-h-0 flex-1'}>
      <CardContent
        className={cn(
          'py-6 text-sm',
          tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground',
          isMobile ? undefined : 'flex min-h-0 flex-1 items-center justify-center',
        )}
      >
        {children}
      </CardContent>
    </Card>
  );
}
